/**
 * Edit this file to add or update portfolio projects.
 *
 * categories: any of "ai", "systems", "products", "education"
 * links: "demo" | "devpost" | "github"
 */
export const projects = [
  {
    categories: ['ai', 'products', 'education'],
    title: 'Summis',
    description:
      'AI college admissions platform with customized strategy reports, roadmap tasks, and Firecrawl-scraped opportunity recommendations grounded in pgvector retrieval.',
    tags: ['Next.js', 'TypeScript', 'Aurora PostgreSQL', 'pgvector', 'Firecrawl'],
    links: [{ type: 'demo', href: 'https://trysummis.com', label: 'Demo' }],
  },
  {
    categories: ['ai', 'products', 'education'],
    title: 'StudySaver',
    description:
      'AI budgeting copilot for college and high school students. Claude gives campus-aware financial advice, Browserbase scrapes university-specific data, and tools cover aid negotiation, syllabus cost scanning, meal-plan burn-down, voice expense logging, and impulse-buy checks.',
    tags: ['Next.js', 'TypeScript', 'Claude', 'Browserbase', 'Deepgram', 'Tailwind CSS'],
    links: [
      { type: 'demo', href: 'https://study-saver.vercel.app/', label: 'Demo' },
      { type: 'devpost', href: 'https://devpost.com/software/studysaver', label: 'Devpost' },
      { type: 'github', href: 'https://github.com/cadelew/StudySaver', label: 'GitHub' },
    ],
  },
  {
    categories: ['ai', 'products'],
    title: 'Tendly',
    description:
      'AI caregiver assistant with voice agents, Claude triage, Redis-backed long-term memory, and caregiver alerts. 1st Place, Simular Track at the 2026 UC Berkeley AI Hackathon.',
    tags: ['Next.js', 'FastAPI', 'Claude', 'Redis', 'Deepgram', 'Simulang'],
    links: [{ type: 'devpost', href: 'https://devpost.com/software/tendly-smolpq', label: 'Devpost' }],
  },
  {
    categories: ['systems'],
    title: 'Crypto Market Making Engine',
    description:
      'C++20 Avellaneda–Stoikov quoting engine with EWMA volatility, micro-price fair value, and lock-free DB writes on live Binance WebSocket data.',
    tags: ['C++20', 'libwebsockets', 'FastAPI', 'Python'],
    links: [
      {
        type: 'github',
        href: 'https://github.com/cadelew/Crypto-Market-Making-Engine',
        label: 'GitHub',
      },
    ],
  },
  {
    categories: ['ai', 'products', 'education'],
    title: 'CS @ SJDLS',
    description:
      'Computer-science learning platform with adaptive quizzes, rubric checklists, progress persistence, and generated practice questions for 100 active users.',
    tags: ['React', 'Express', 'MongoDB', 'Firebase', 'Node.js'],
    links: [{ type: 'github', href: 'https://github.com/cadelew/cs-at-sjdls', label: 'GitHub' }],
  },
];

const CATEGORY_LABELS = {
  ai: 'AI',
  systems: 'Systems',
  products: 'Products',
  education: 'Education',
};

const ICONS = {
  demo: `<svg viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>`,
  devpost: `<svg viewBox="0 0 24 24" aria-hidden="true" fill="currentColor"><path d="M12 1.5 3.5 6.5v11l8.5 5 8.5-5v-11L12 1.5zm0 2.2 6.5 3.8v7l-6.5 3.8L5.5 14.5v-7L12 3.7z"/></svg>`,
  github: `<svg viewBox="0 0 24 24" aria-hidden="true" fill="currentColor"><path d="M12 2C6.477 2 2 6.584 2 12.217c0 4.506 2.865 8.33 6.839 9.679.5.092.682-.22.682-.486 0-.24-.009-.875-.014-1.717-2.782.615-3.369-1.367-3.369-1.367-.454-1.178-1.11-1.491-1.11-1.491-.908-.635.069-.622.069-.622 1.003.072 1.532 1.053 1.532 1.053.892 1.56 2.341 1.11 2.91.849.091-.66.35-1.11.636-1.365-2.22-.258-4.555-1.135-4.555-5.05 0-1.115.39-2.027 1.03-2.742-.103-.258-.447-1.297.098-2.703 0 0 .84-.274 2.75 1.046A9.36 9.36 0 0 1 12 6.844a9.36 9.36 0 0 1 2.504.344c1.909-1.32 2.748-1.046 2.748-1.046.546 1.406.202 2.445.1 2.703.64.715 1.028 1.627 1.028 2.742 0 3.925-2.339 4.788-4.566 5.043.359.316.679.94.679 1.895 0 1.368-.012 2.471-.012 2.807 0 .269.18.583.688.484C19.138 20.543 22 16.72 22 12.217 22 6.584 17.523 2 12 2Z"/></svg>`,
};

function formatIndex(index) {
  return String(index + 1).padStart(2, '0');
}

function formatCategories(categories) {
  return categories.map((key) => CATEGORY_LABELS[key] ?? key).join(' · ');
}

function renderLinks(links, title) {
  if (!links.length) return '';

  const items = links
    .map((link) => {
      const icon = ICONS[link.type] ?? ICONS.demo;
      const external = link.href.startsWith('http');
      const attrs = external ? ' target="_blank" rel="noreferrer"' : '';
      return `<a href="${link.href}"${attrs} aria-label="${title} ${link.label}">${icon}${link.label}</a>`;
    })
    .join('');

  return `<div class="project-links">${items}</div>`;
}

export function renderProjects(container) {
  if (!(container instanceof HTMLElement)) return;

  container.innerHTML = projects
    .map((project, index) => {
      const categories = project.categories.join(' ');
      const tags = project.tags.map((tag) => `<li>${tag}</li>`).join('');
      const links = renderLinks(project.links ?? [], project.title);

      return `<article class="project-card" data-categories="${categories}">
        <div class="project-topline"><span>${formatIndex(index)}</span><span>${formatCategories(project.categories)}</span></div>
        <h3>${project.title}</h3>
        <p>${project.description}</p>
        <ul class="tags">${tags}</ul>
        ${links}
      </article>`;
    })
    .join('');
}

export function initProjects() {
  const container = document.querySelector('[data-project-grid]');
  if (!(container instanceof HTMLElement)) return;
  renderProjects(container);
}
